import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { t } from '../utils/translations';
import styles from './DashboardSidebar.module.css';

const PAGE_NAMES = {
  '/dashboard': 'Dashboard',
  '/create-lesson-plan/form': 'Create A Lesson Plan',
  '/generated-material': 'My Plans',
  '/my-plans': 'My Plans',
  '/about-help': 'About & Help',
};

/** Prefill options for "Which part of this page were you using?" per page */
const PAGE_ELEMENTS = {
  '/dashboard': [
    'Welcome message',
    'Generations counter',
    'Create lesson plan',
    'My plans',
    'About & Help',
  ],
  '/create-lesson-plan/form': [
    'Lesson info form',
    'Subject / grade / standard selector',
    'Language selector',
    'Cultural connections',
    'Next button',
  ],
  '/generated-material': [
    'Plans list',
    'Create new lesson plan',
    'In progress / Completed tabs',
    'Plan cards',
  ],
  '/my-plans': [
    'Plans list',
    'Create new lesson plan',
    'In progress / Completed tabs',
    'Plan cards',
  ],
  '/about-help': [
    'FAQ',
    'Help content',
    'Links',
  ],
};

function DashboardSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { openFeedbackPanel } = useFeedback();

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Lesson generator links directly to form; intermediate selection skipped (augment + quiz flows hidden).
  const navItems = [
    { path: '/dashboard', key: 'dashboard' },
    { path: '/create-lesson-plan/form', key: 'tools' },
    // HIDDEN: Quiz feature temporarily disabled
    // { path: '/generate-quiz', key: 'generate_quiz' },
    { path: '/generated-material', key: 'my_plans' },
    { path: '/about-help', key: 'about_help' },
  ];

  const handleFeedback = () => {
    const pageId = location.pathname.replace(/^\//, '') || 'dashboard';
    const pageName = PAGE_NAMES[location.pathname] || location.pathname;
    const page_elements = PAGE_ELEMENTS[location.pathname] || [];
    openFeedbackPanel('screen', { page_id: pageId, page_name: pageName, page_elements });
  };

  return (
    <div 
      data-property-1="Default" 
      style={{
        width: '100%', 
        height: '100%', 
        paddingTop: '2rem', 
        paddingBottom: '2rem', 
        borderTopRightRadius: '1rem', 
        flexDirection: 'column', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        display: 'flex',
        background: '#F9FAFF'
      }}
    >
      <div style={{
        width: '100%', 
        padding: '2rem', 
        background: 'white', 
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)', 
        overflow: 'hidden', 
        borderTopRightRadius: '1rem', 
        borderBottomRightRadius: '1rem', 
        borderTop: '1px #2EC974 solid', 
        borderRight: '1px #2EC974 solid', 
        borderBottom: '1px #2EC974 solid', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        gap: '0.75rem', 
        display: 'flex'
      }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <div
              key={item.path}
              data-active={active ? "on" : "off"}
              onClick={() => navigate(item.path)}
              style={{
                width: '100%', 
                paddingLeft: '0.75rem', 
                paddingRight: '0.75rem', 
                paddingTop: '0.5rem', 
                paddingBottom: '0.5rem', 
                background: active ? '#E5FFD0' : '#F8F4F2', 
                boxShadow: active ? '0px 4px 4px rgba(0, 0, 0, 0.25) inset' : 'none',
                borderRadius: '0.75rem', 
                outline: active ? '1px #2EC974 solid' : '1px #D4CFCD solid', 
                outlineOffset: '-1px', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'flex-start', 
                display: 'flex',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '100%', 
                color: active ? '#092F3E' : '#4F4C4A', 
                fontSize: 'clamp(1.125rem, 1.5vw, 1.5rem)', 
                fontFamily: 'Open Sans', 
                fontWeight: '600', 
                textTransform: 'capitalize', 
                wordWrap: 'break-word',
                lineHeight: '1.2'
              }}>
                {t(item.key, language)}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        width: '18%',
        minWidth: '200px',
        maxWidth: '280px',
        padding: '2rem',
        background: 'white',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
        borderTopRightRadius: '1rem',
        borderBottomRightRadius: '1rem',
        borderTop: '1px #62C9FF solid',
        borderRight: '1px #62C9FF solid',
        borderBottom: '1px #62C9FF solid',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        gap: '0.5rem',
        display: 'flex',
        position: 'fixed',
        left: 0,
        bottom: '24px',
        zIndex: 999
      }}>
        <div
          data-color="Blue (Beta/Testing)"
          data-icon-side="Off"
          data-size="Large"
          data-status="Default"
          data-type="Primary"
          onClick={handleFeedback}
          style={{
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
            paddingLeft: '0.75rem',
            paddingRight: '1rem',
            background: 'linear-gradient(131deg, #33A3FF 16%, #5773AE 100%)',
            borderRadius: '1.5rem',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            display: 'flex',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          <div style={{ width: '1.5rem', height: '1.5rem', position: 'relative', flexShrink: 0 }}>
            <img
              src="/assets/light_bulb.svg"
              alt="Feedback"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          </div>
          <div style={{
            textAlign: 'center',
            color: '#FFFCF9',
            fontSize: 'clamp(0.875rem, 1vw, 1rem)',
            fontFamily: 'Open Sans',
            fontWeight: '600',
            textTransform: 'uppercase',
            lineHeight: '1.5',
            wordWrap: 'break-word',
            whiteSpace: 'nowrap'
          }}>
            {t('feedback', language)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardSidebar;
